import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import {
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nabvar from "../../common/Nabvar";
import Sidebar from "../../common/Sidebar";
import axios from "../../config/authaxios";
import { useStyles } from "../../theme/theme";
import Body from "../../ui/Body";
import DeleteDiscount from "./DeleteDiscount";
import {
  fetchEligibleVouchers,
  formatDenominationsSummary,
  formatToIST,
  getCategoryTypeLabel,
} from "./discountUtils";

export default function DiscountList() {
  const navigate = useNavigate();
  const classes = useStyles();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);

  const [publishers, setPublishers] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [publisherFilter, setPublisherFilter] = useState("");
  const [skuFilter, setSkuFilter] = useState("");
  const [isDeletedFilter, setIsDeletedFilter] = useState("false");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: "", sku: "", isDeleted: false });

  const isDeletedView = isDeletedFilter === "true";

  const publisherMap = useMemo(() => {
    const map = {};
    publishers.forEach((p) => {
      map[p._id] = p.name;
    });
    return map;
  }, [publishers]);

  const voucherMap = useMemo(() => {
    const map = {};
    vouchers.forEach((v) => {
      map[v.sku] = v.name;
    });
    return map;
  }, [vouchers]);

  const fetchPublishers = () => {
    axios.get("/admin/publishers/name-id").then((res) => {
      if (res.data.status_code === 401) localStorage.clear();
      if (res.data.status) {
        setPublishers(res.data.response || []);
      }
    });
  };

  const fetchVouchers = () => {
    fetchEligibleVouchers(axios).then(setVouchers);
  };

  const fetchDiscounts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      is_deleted: isDeletedFilter,
    });
    if (publisherFilter) params.set("publisher_id", publisherFilter);
    if (skuFilter.trim()) params.set("sku", skuFilter.trim());

    axios
      .get(`/admin/discount?${params.toString()}`)
      .then((res) => {
        if (res.data.status_code === 401) localStorage.clear();
        if (res.data.status) {
          const data = res.data.response;
          setItems(data.items || []);
          setTotal(data.total || 0);
        } else {
          setItems([]);
          setTotal(0);
        }
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, limit, publisherFilter, skuFilter, isDeletedFilter]);

  useEffect(() => {
    fetchPublishers();
    fetchVouchers();
  }, []);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  const openDeleteModal = (row) => {
    setDeleteTarget({
      id: row._id,
      sku: row.sku,
      isDeleted: row.is_deleted || isDeletedView,
    });
    setDeleteOpen(true);
  };

  const columns = [
    {
      field: "s_no",
      headerName: "S. No.",
      headerClassName: "super-app-theme--header",
      width: 70,
      filterable: false,
      renderCell: (index) =>
        index.api?.getRowIndexRelativeToVisibleRows(index.row._id) + 1 + page * limit,
    },
    {
      field: "publisher_id",
      headerName: "Publisher",
      headerClassName: "super-app-theme--header",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => publisherMap[params.row.publisher_id] || params.row.publisher_id,
    },
    {
      field: "sku",
      headerName: "SKU",
      headerClassName: "super-app-theme--header",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "category_type",
      headerName: "Category",
      headerClassName: "super-app-theme--header",
      width: 100,
      valueGetter: (params) => getCategoryTypeLabel(params.row.category_type),
    },
    {
      field: "voucher_name",
      headerName: "Voucher Name",
      headerClassName: "super-app-theme--header",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => voucherMap[params.row.sku] || "—",
    },
    {
      field: "discount_enabled",
      headerName: "Enabled",
      headerClassName: "super-app-theme--header",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.discount_enabled ? "Yes" : "No"}
          color={params.row.discount_enabled ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "discount_type",
      headerName: "Type",
      headerClassName: "super-app-theme--header",
      width: 90,
    },
    {
      field: "denominations",
      headerName: "Denominations",
      headerClassName: "super-app-theme--header",
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => formatDenominationsSummary(params.row.denominations),
    },
    {
      field: "max_discount_cap",
      headerName: "Cap (₹)",
      headerClassName: "super-app-theme--header",
      width: 90,
      valueGetter: (params) =>
        params.row.max_discount_cap > 0 ? params.row.max_discount_cap : "—",
    },
    {
      field: "cashback_percent",
      headerName: "Cashback %",
      headerClassName: "super-app-theme--header",
      width: 120,
      valueGetter: (params) =>
        params.row.cashback_percent == null ? "Inherit global" : `${params.row.cashback_percent}%`,
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      headerClassName: "super-app-theme--header",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => formatToIST(params.row.updated_at),
    },
    {
      field: "edit",
      headerName: "Edit",
      headerClassName: "super-app-theme--header",
      width: 70,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={() =>
            navigate(`/discounts/${params.row._id}/edit?is_deleted=${isDeletedFilter}`)
          }
        >
          <EditIcon color="primary" />
        </IconButton>
      ),
    },
    {
      field: "delete",
      headerName: "Delete",
      headerClassName: "super-app-theme--header",
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const deleted = params.row.is_deleted || isDeletedView;
        return (
          <IconButton onClick={() => openDeleteModal(params.row)}>
            {deleted ? (
              <RestoreFromTrashIcon color="success" />
            ) : (
              <DeleteIcon color="error" />
            )}
          </IconButton>
        );
      },
    },
  ];

  return (
    <>
      <Sidebar />
      <Nabvar />
      <Body>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ my: 2 }}>
          <Typography variant="h5">Publisher Discounts</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => navigate("/discounts/bulk")}>
              Bulk Assign
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/discounts/new")}
            >
              Add Discount
            </Button>
          </Stack>
        </Stack>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
            <FormControl sx={{ minWidth: 220 }} size="small">
              <InputLabel>Publisher</InputLabel>
              <Select
                value={publisherFilter}
                label="Publisher"
                onChange={(e) => {
                  setPublisherFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All Publishers</MenuItem>
                {publishers.map((p) => (
                  <MenuItem key={p._id} value={p._id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="SKU (exact match)"
              value={skuFilter}
              onChange={(e) => {
                setSkuFilter(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 200 }}
            />
            <FormControl sx={{ minWidth: 160 }} size="small">
              <InputLabel>Is Deleted</InputLabel>
              <Select
                value={isDeletedFilter}
                label="Is Deleted"
                onChange={(e) => {
                  setIsDeletedFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="false">false</MenuItem>
                <MenuItem value="true">true</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={() => {
                setPage(0);
                fetchDiscounts();
              }}
            >
              Search
            </Button>
          </Stack>
        </Paper>

        <Paper
          sx={{
            bgcolor: "#fff",
            "& .super-app-theme--header": {
              bgcolor: "#DC8449",
              color: "#fff",
            },
          }}
          elevation={0}
        >
          <DataGrid
            className={classes.root}
            sx={{ border: 0 }}
            autoHeight
            rows={items}
            columns={columns}
            getRowId={(row) => row._id}
            loading={loading}
            disableRowSelectionOnClick
            paginationMode="server"
            rowCount={total}
            page={page}
            pageSize={limit}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => {
              setLimit(newSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Paper>
      </Body>

      <DeleteDiscount
        open={deleteOpen}
        setOpen={setDeleteOpen}
        discountId={deleteTarget.id}
        sku={deleteTarget.sku}
        isDeleted={deleteTarget.isDeleted}
        onDeleted={fetchDiscounts}
      />
    </>
  );
}
